import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// Cierre de sesión automático por inactividad (2 horas).
const INACTIVITY_LIMIT_MS = 2 * 60 * 60 * 1000;
const ACTIVITY_KEY = "odf_last_activity";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("odf_token"));
  const [loading, setLoading] = useState(true);
  const lastWriteRef = useRef(0);

  const api = useMemo(() => {
    const instance = axios.create({ baseURL: API_BASE });
    instance.interceptors.request.use((config) => {
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
    instance.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 401) {
          setUser(null);
          setToken(null);
          localStorage.removeItem("odf_token");
        }
        return Promise.reject(err);
      }
    );
    return instance;
  }, [token]);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    api.get("/api/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => { setToken(null); localStorage.removeItem("odf_token"); })
      .finally(() => setLoading(false));
  }, [token, api]);

  const logout = useCallback(() => {
    localStorage.removeItem("odf_token");
    localStorage.removeItem(ACTIVITY_KEY);
    setToken(null);
    setUser(null);
  }, []);

  // --- Cierre automático por inactividad ---
  useEffect(() => {
    if (!token) return;

    const markActivity = () => {
      localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
    };

    const isExpired = () => {
      const last = parseInt(localStorage.getItem(ACTIVITY_KEY) || "0", 10);
      return last > 0 && Date.now() - last > INACTIVITY_LIMIT_MS;
    };

    const expireSession = () => {
      localStorage.setItem("odf_session_expired", "1");
      logout();
    };

    // Si al abrir ya pasaron 2h sin actividad, cerrar sesión.
    if (isExpired()) { expireSession(); return; }
    markActivity();
    lastWriteRef.current = Date.now();

    const onActivity = () => {
      const now = Date.now();
      // Throttle: registrar actividad como máximo cada 30s.
      if (now - lastWriteRef.current > 30000) {
        lastWriteRef.current = now;
        markActivity();
      }
    };
    const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    const interval = setInterval(() => {
      if (isExpired()) expireSession();
    }, 60000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      clearInterval(interval);
    };
  }, [token, logout]);

  const login = async (email, password) => {
    const res = await api.post("/api/auth/login", { email, password });
    const { access_token, user: userData } = res.data;
    localStorage.setItem("odf_token", access_token);
    localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
    localStorage.removeItem("odf_session_expired");
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const isAdmin = user?.cargo === "admin";

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, isAdmin, api }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
